
import Vue from "vue";
import { SaleEditState } from "@/utils/sale-edit/sale-edit-state";
import { LoyaltyConfig } from "@/models/dossier-configs/loyalty";
import { DossierConfigService } from "@/services/dossier-config-service";
import { CustomerService } from "@/services/customer-service";
import { ILoyaltyTransaction, LoyaltyService } from "@/services/loyalty-service";
import { UserConfigService } from "@/services/user-config-service";
import { Permissions } from "@/models/user-configs/permissions";
import { printLocalDate } from "@/utils/pretty-print";
import { TranslateResult } from "vue-i18n";

export default Vue.extend({
	data(){
		return {
			activeTab: 0,
			redeemPoints: 0,
			manualPoints: 0,
			saving: false,
			showCard: false,
			loadingCard: false,
			cardImageUrl: "",
			transactions: [] as ILoyaltyTransaction[]
		};
	},
	props: {
		state: {
			type: Object as () => SaleEditState,
			required: true
		}
	},
	computed: {
		config():LoyaltyConfig {
			return DossierConfigService.getLoyaltyConfig();
		},
		cardBarcode():string {
			return this.state.sale.Contact?.Barcode || "";
		},
		balance():number {
			return this.state.loyalty.balance || 0;
		},
		pointValue():number {
			return this.config.getPointValue();
		},
		balanceValue():number {
			return this.balance * this.pointValue;
		},
		earnPreview():number {
			return this.state.loyalty.getEarnPreview();
		},
		nextRewardPoints():number {
			return this.config.getNextRewardPoints(this.balance);
		},
		pointsToGo():number {
			return Math.max(0, this.nextRewardPoints - this.balance);
		},
		progress():number {
			if (this.nextRewardPoints <= 0) return 0;
			return Math.max(0, Math.min(100, this.balance / this.nextRewardPoints * 100));
		},
		redeemAllowed():boolean {
			if (!this.state.loyalty.isRedeemAllowed()) return false;
			if (this.state.loyalty.getRedemptionRow()) return false;
			return this.redeemOptions.length > 0;
		},
		discountableBase():number {
			let total = 0;
			for (let row of this.state.sale.Rows) {
				if (row.SaleRowConfig.LoyaltyRedemption) continue;
				if (row.IsTaxRow) continue;
				total += row.TotalPriceIncVat;
			}
			return Math.max(0, total);
		},
		balanceRedeemPoints():number {
			return this.config.getRedeemableSteps(this.balance) * this.config.RedeemStepPoints;
		},
		saleRedeemPoints():number {
			if (this.config.RedeemStepEuro <= 0) return 0;
			return Math.floor(this.discountableBase / this.config.RedeemStepEuro) * this.config.RedeemStepPoints;
		},
		maxRedeemPoints():number {
			return Math.max(0, Math.min(this.balanceRedeemPoints, this.saleRedeemPoints));
		},
		redeemOptions():number[] {
			let options:number[] = [];
			for (let points = this.config.RedeemStepPoints; points <= this.balanceRedeemPoints; points += this.config.RedeemStepPoints) {
				options.push(points);
			}
			return options;
		},
		discount():number {
			return this.redeemPoints * this.pointValue;
		},
		isAdmin():boolean {
			return UserConfigService.hasCurrentUserPermission((permissions:Permissions):boolean=>{
				return permissions.IsAdmin;
			});
		},
		tabs():TranslateResult[] {
			return [this.$ct("tab-overview"), this.$ct("tab-history")];
		}
	},
	watch: {
		maxRedeemPoints(){
			if (this.redeemPoints > this.maxRedeemPoints) {
				this.redeemPoints = this.maxRedeemPoints;
			}
		}
	},
	methods: {
		close(){
			this.state.loyalty.modalOpen = false;
		},
		async openCard(){
			if (this.loadingCard) return;
			this.loadingCard = true;
			try {
				if (this.cardImageUrl) {
					URL.revokeObjectURL(this.cardImageUrl);
					this.cardImageUrl = "";
				}
				this.cardImageUrl = await CustomerService.getLoyaltyBarcodeImageUrl(this.state.sale.ContactID);
				this.showCard = true;
			} finally {
				this.loadingCard = false;
			}
		},
		selectRedeemPoints(points:number){
			if (points > this.maxRedeemPoints) return;
			if (this.redeemPoints == points) {
				this.redeemPoints = 0;
				return;
			}
			this.redeemPoints = points;
		},
		async redeem(){
			if (this.config.RedeemStepPoints <= 0) return;
			let points = Math.min(this.redeemPoints, this.maxRedeemPoints);
			let steps = Math.floor(points / this.config.RedeemStepPoints);
			if (steps <= 0) return;
			await this.state.loyalty.addRedemptionRow(steps);
			this.close();
		},
		printDate(date:Date):string {
			return printLocalDate(date);
		},
		formatDocument(transaction:ILoyaltyTransaction):string {
			if (!transaction.SaleNumber) {
				return this.$ct("manual").toString();
			}
			return [transaction.SaleTypeName, transaction.SaleNumber].filter(v=>v).join(" ");
		},
		async loadTransactions(){
			if (!this.state.sale.ContactID) return;
			this.transactions = await LoyaltyService.getTransactions(this.state.sale.ContactID);
		},
		async addManualPoints(){
			if (this.saving || this.manualPoints == 0) return;
			try {
				this.saving = true;
				await LoyaltyService.addManualPoints(this.state.sale.ContactID, this.manualPoints);
				this.state.loyalty.balance = (this.state.loyalty.balance || 0) + this.manualPoints;
				this.manualPoints = 0;
				await this.loadTransactions();
			} catch (err) {
			}
			this.saving = false;
		}
	},
	mounted(){
		this.redeemPoints = this.maxRedeemPoints;
		this.loadTransactions();
	},
	i18nEx: {
		componentPrefix: "components.sales.loyalty"
	}
});
